import React, { useState, useEffect } from "react";
import { Spinner, Form, Row, Col, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "../../Styles/yellocalfcss/SelectStudent.css";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall } from "../../api/ApiServices";

const GenerateArrearInvoice = () => {
  const navigate = useNavigate();

  const [grades, setGrades] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [studentList, setStudentList] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentValues, setStudentValues] = useState({});
  
  // --- Validation State ---
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    grade: "",
    academicYear: "",
    term: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);

  useEffect(() => {
    const loadInitialData = async () => {
      setIsFetching(true);
      await Promise.all([fetchGrades(), fetchAcademicYears(), fetchTerms()]);
      setIsFetching(false);
    };
    loadInitialData();
  }, []);

  // API Fetch Functions (fetchGrades, fetchTerms, fetchAcademicYears) remain the same...
  const fetchGrades = async () => {
    try {
      const res = await simplePostCall(ApiConfig.classlist, JSON.stringify({
        customer_id: Number(localStorage.getItem("customer_id")),
        api_key: localStorage.getItem("api_key"),
      }));
      if (res?.classread) setGrades(res.classread);
    } catch (err) { console.error(err); }
  };

  const fetchTerms = async () => {
    try {
      const res = await simplePostCall(ApiConfig.termstype, JSON.stringify({
        customer_id: Number(localStorage.getItem("customer_id")),
        api_key: localStorage.getItem("api_key"),
      }));
      if (res?.master_academic) setTerms(res.master_academic);
    } catch (err) { console.error(err); }
  };

  const fetchAcademicYears = async () => {
    try {
      const res = await simplePostCall(ApiConfig.academicyear, JSON.stringify({
        customer_id: Number(localStorage.getItem("customer_id")),
        api_key: localStorage.getItem("api_key"),
        language: "en",
      }));
      if (res?.academicYear) setAcademicYears(res.academicYear);
    } catch (err) { console.error(err); }
  };

  const handleGradeChange = async (e) => {
    const gradeId = e.target.value;
    setFormData({ ...formData, grade: gradeId });
    setStudentList([]);
    setSelectedStudents([]);
    setStudentValues({});
    setErrors({}); // Reset errors on grade change

    if (gradeId) {
      setIsTableLoading(true);
      try {
        const res = await simplePostCall(ApiConfig.Studentclasslist, JSON.stringify({
          customer_id: Number(localStorage.getItem("customer_id")),
          api_key: localStorage.getItem("api_key"),
          class_id: gradeId,
          language: "en",
        }));
        if (res?.result && res.academicYear) setStudentList(res.academicYear);
      } catch (err) { console.error(err); }
      finally { setIsTableLoading(false); }
    }
  };

  const handleInputChange = (studentId, field, value) => {
    setStudentValues((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], [field]: value },
    }));
    // Clear error for this field as user types
    if (errors[`${studentId}_${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`${studentId}_${field}`];
      setErrors(newErrors);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedStudents(studentList.map((s) => s.student_id));
    } else {
      setSelectedStudents([]);
      setErrors({}); // Clear validation errors if nothing is selected
    }
  };

  const handleStudentSelect = (id) => {
    setSelectedStudents((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // --- Validation Logic ---
  const validateForm = () => {
    const newErrors = {};

    // 1. Check Header Dropdowns
    if (!formData.academicYear) newErrors.academicYear = true;
    if (!formData.grade) newErrors.grade = true;

    // 2. Check Selected Students data
    if (selectedStudents.length === 0) {
      alert("Please select at least one student.");
      return false;
    }

    selectedStudents.forEach((id) => {
      const val = studentValues[id] || {};
      
      // Amount is Mandatory if student is selected
      if (!val.amount || parseFloat(val.amount) <= 0) {
        newErrors[`${id}_amount`] = "Required > 0";
      }
      
      // Bonus/Discount shouldn't be negative
      if (val.discount && parseFloat(val.discount) < 0) {
        newErrors[`${id}_discount`] = "Negative not allowed";
      }
      if (val.bonus && parseFloat(val.bonus) < 0) {
        newErrors[`${id}_bonus`] = "Negative not allowed";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return; // Stop if validation fails
    }

    setIsLoading(true);
    const amounts = {};
    const bonuses = {};
    const discounts = {};

    selectedStudents.forEach((id) => {
      amounts[id] = studentValues[id]?.amount || "0";
      bonuses[id] = studentValues[id]?.bonus || "0";
      discounts[id] = studentValues[id]?.discount || "0";
    });

    const payload = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      language: "en",
      academic: formData.academicYear,
      terms: formData.term,
      students: selectedStudents,
      amount: amounts,
      bonus: bonuses,
      discount: discounts,
    };

    try {
      const res = await simplePostCall(ApiConfig.feeinvoiceAdd, JSON.stringify(payload));
      if (res?.result) {
        alert("Arrear invoices generated successfully!");
        navigate("/UpcomingInvoices");
      } else {
        alert(res?.message || "Something went wrong");
      }
    } catch (err) {
      console.error("Submit failed", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="SelectStudent-container my-4">
      <div className="container-fluid bg-white shadow-sm py-3 px-4 mb-3 rounded">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
          <h4 className="mb-2 mb-md-0 text-dark">Generate Arrear Invoice</h4>
          <button className="btn py-2 custom-btn" onClick={() => navigate("/import-invoice")}>
            Import Arrears
          </button>
        </div>
      </div>

      <div className="SelectStudent-invoice-form mt-3">
        <Form onSubmit={handleSubmit}>
          <Row className="mb-4">
            <Col md={4} className="mb-3">
              <Form.Label>Select Grade <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={formData.grade}
                onChange={handleGradeChange}
                isInvalid={!!errors.grade}
              >
                <option value="">Select Grade</option>
                {grades.map((g) => (
                  <option key={g.class_id} value={g.class_id}>{g.class_name}</option>
                ))}
              </Form.Select>
            </Col>

            <Col md={4} className="mb-3">
              <Form.Label>Select Academic Year <span className="text-danger">*</span></Form.Label>
              <Form.Select
                value={formData.academicYear}
                onChange={(e) => setFormData({ ...formData, academicYear: e.target.value })}
                isInvalid={!!errors.academicYear}
              >
                <option value="">Academic Year</option>
                {academicYears.map((y) => (
                  <option key={y.academic_year_id} value={y.academic_year_id}>{y.academic_year_name}</option>
                ))}
              </Form.Select>
            </Col>

            <Col md={4} className="mb-3">
              <Form.Label>Select Terms/Semester</Form.Label>
              <Form.Select
                value={formData.term}
                onChange={(e) => setFormData({ ...formData, term: e.target.value })}
              >
                <option value="">Select Terms/Semester</option>
                {terms.filter((t) => t.terms_status === "active").map((t) => (
                  <option key={t.terms_id} value={t.terms_id}>{t.terms_name}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          <div className="student-table-wrapper mt-4">
            {isTableLoading ? (
              <div className="text-center py-4"><Spinner animation="border" variant="primary" /></div>
            ) : studentList.length > 0 ? (
              <Table responsive striped bordered hover className="mt-2 align-middle">
                <thead className="bg-light">
                  <tr>
                    <th style={{ width: "50px" }}>
                      <Form.Check
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={selectedStudents.length === studentList.length && studentList.length > 0}
                      />
                    </th>
                    <th>Students</th>
                    <th>Amount <span className="text-danger">*</span></th>
                    <th>Discount</th>
                    <th>Bonus</th>
                  </tr>
                </thead>
                <tbody>
                  {studentList.map((student) => {
                    const isSelected = selectedStudents.includes(student.student_id);
                    return (
                      <tr key={student.student_id} className={isSelected ? "table-primary-light" : ""}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleStudentSelect(student.student_id)}
                          />
                        </td>
                        <td>
                          <div className="fw-bold">{student.student_name}</div>
                          <small className="text-muted">{student.student_admission_num || student.student_id}</small>
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            placeholder="Amount"
                            disabled={!isSelected}
                            isInvalid={!!errors[`${student.student_id}_amount`]}
                            value={studentValues[student.student_id]?.amount || ""}
                            onChange={(e) => handleInputChange(student.student_id, "amount", e.target.value)}
                          />
                          <Form.Control.Feedback type="invalid">Required</Form.Control.Feedback>
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            placeholder="Discount"
                            disabled={!isSelected}
                            isInvalid={!!errors[`${student.student_id}_discount`]}
                            value={studentValues[student.student_id]?.discount || ""}
                            onChange={(e) => handleInputChange(student.student_id, "discount", e.target.value)}
                          />
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            placeholder="Bonus"
                            disabled={!isSelected}
                            isInvalid={!!errors[`${student.student_id}_bonus`]}
                            value={studentValues[student.student_id]?.bonus || ""}
                            onChange={(e) => handleInputChange(student.student_id, "bonus", e.target.value)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : formData.grade && !isTableLoading && (
              <div className="text-center py-3 text-muted">No students found for this grade.</div>
            )}
          </div>

          <div className="d-flex justify-content-end mt-4">
            <button
              type="submit"
              className="btn btn-primary px-5 py-2 fw-bold"
              disabled={isLoading || selectedStudents.length === 0}
            >
              {isLoading ? <Spinner size="sm" className="me-2" /> : "Submit"}
            </button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default GenerateArrearInvoice;